import isEqual from 'lodash.isequal';
import { BehaviorSubject, of, ReplaySubject } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    finalize,
    map,
    skip,
    switchMap,
    take,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { isDefined } from 'src/app/@utils/is-defined';
import { removeEmpty } from 'src/app/@utils/remove-empty.helper';

import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, makeStateKey, signal, TransferState } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Filter } from '@core/services/filter/filter';
import { errorMessages } from '@core/services/filter/filter.config';
import { PlatformService } from '@core/services/platform.service';
import { SearchService } from '@core/services/search.service';
import { SiteContextService } from '@core/services/site-context.service';
import { ProductSearchViewModel } from '@models/api-search.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { UspCard } from '@features/product/product.models';
import { SettingsService } from '@core/services/settings.service';
import { RelewiseRetailMediaParams } from '@core/services/relewise/relewise-tracking.model';
import { FilterType } from '@features/product-filter/product-filter.config';
import {
    getRetailMediaProducts,
    processRetailMediaProducts
} from '@core/services/relewise/process-retail-media.helper';

const filterStateKey = makeStateKey<Filter>('filterStateKey');

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class FilterService {
    private uspCardsBs: BehaviorSubject<UspCard[]> = new BehaviorSubject<UspCard[]>([]);
    private filterBs = new BehaviorSubject(this.intialState);
    private filterListBs = new BehaviorSubject<ProductSearchViewModel[] | undefined>([]);
    private queryParamsBs = new BehaviorSubject<Params>({});
    private pendingBs = new BehaviorSubject(false);
    private errorRs: ReplaySubject<HttpErrorResponse | null> = new ReplaySubject(1);
    private filterConfigurationId: string | null;
    private isLoadMore: boolean = false;
    private initial: boolean = true;
    private relewiseRetailMediaParams?: RelewiseRetailMediaParams;

    public readonly defaultTakeAmount = 72;
    public readonly filterParamKey = 'filter';

    public readonly filter$ = this.filterBs.asObservable();
    public readonly filterList$ = this.filterListBs.asObservable();
    public readonly queryParams$ = this.queryParamsBs.asObservable();
    public readonly pending$ = this.pendingBs.asObservable();
    public readonly error$ = this.errorRs.asObservable();
    public readonly retailMediaResults = signal<ProductSearchViewModel[]>([]);

    constructor(
        private router: Router,
        private gtmService: GtmService,
        private searchService: SearchService,
        private platformService: PlatformService,
        private transferState: TransferState,
        private location: Location,
        private relewiseTrackingService: RelewiseTrackingService,
        private siteContextService: SiteContextService,
        private settingsService: SettingsService
    ) {
        this.queryParamsBs
            .pipe(
                skip(1),
                filter(() => Boolean(this.filterConfigurationId)),
                withLatestFrom(this.uspCardsBs),
                switchMap(([params, uspCards]) => {
                    const state = this.transferState.get(filterStateKey, null);

                    if (this.initial && state) {
                        this.initial = false;
                        return of(state);
                    }

                    this.pendingBs.next(true);

                    const { temporaryId, consent } = this.relewiseTrackingService.getRelewiseTrackingParams();
                    if (consent) {
                        params = { ...params, temporaryId, consent };
                    }

                    // we need to make sure we only fetch the number of products that we can show on the page,
                    // so we need to calculate the amount of products we can show without usp cards,
                    // but we should not do this if we are in load more state (from parameter is set)
                    const takeParam = parseInt(params?.take, 10);
                    const takeWithoutUspCards =
                        (isNaN(takeParam) ? this.defaultTakeAmount : takeParam) - uspCards.length;
                    if (!params.from && uspCards.length) {
                        params = { ...params, take: takeWithoutUspCards };
                    }

                    if (this.relewiseRetailMediaParams?.filterType === FilterType.SearchResult) {
                        const { filterType, ...rest } = this.relewiseRetailMediaParams;

                        params = { ...params, ...rest };
                    }

                    return this.searchService
                        .getFilterCollection(this.filterConfigurationId as string, params)
                        .pipe(finalize(() => this.pendingBs.next(false)));
                }),
                map(response => {
                    if (this.platformService.isServer) {
                        this.transferState.set(filterStateKey, response);
                    }
                    return response;
                }),
                untilDestroyed(this)
            )
            .subscribe({
                next: model => this.filterBs.next({ ...model, loadMore: this.isLoadMore, initial: false }),
                error: error => this.errorRs.next(error)
            });

        this.filter$
            .pipe(
                switchMap(filterResponse =>
                    this.filterList$.pipe(
                        untilDestroyed(this),
                        take(1),
                        tap(() => {
                            const products = getRetailMediaProducts(
                                processRetailMediaProducts(filterResponse),
                                this.relewiseRetailMediaParams?.retailMediaPlacements
                            );
                            this.retailMediaResults.set(products);
                        }),
                        map(previousList => {
                            return filterResponse.loadMore && filterResponse.products && previousList
                                ? [...previousList, ...filterResponse.products]
                                : filterResponse.products;
                        })
                    )
                ),
                untilDestroyed(this)
            )
            .subscribe(products => this.filterListBs.next(products));
    }

    public initFilterConfiguration(id: string, params: Params = {}): void {
        // When going back and forth from product detail page, we keep state
        if (this.filterConfigurationId === id && this.siteContextService.lastNavigationWasPopstate) {
            return;
        }

        this.destroy();
        this.filterConfigurationId = id;

        // we need to fetch the usp cards first based on the filter configuration id
        // to make sure we can use it later, so we only can fetch after having that information
        this.settingsService
            .getPLPUspCard(id)
            .pipe(untilDestroyed(this))
            .subscribe(uspCards => {
                this.uspCardsBs.next(uspCards ?? []);

                this.queryParamsBs.next(params);
            });
    }

    public setRelewiseRetailMediaParams(params: Partial<RelewiseRetailMediaParams>): void {
        this.relewiseRetailMediaParams = {
            ...(this.relewiseRetailMediaParams ?? {}),
            ...params
        } as RelewiseRetailMediaParams;
    }

    public resetRelewiseRetailMediaParams(): void {
        this.relewiseRetailMediaParams = undefined;
        this.retailMediaResults.set([]);
    }

    public update(currentParams: Params, isLoadMore: boolean = false): void {
        if (
            (isLoadMore && !isDefined<number>(currentParams.take)) ||
            (isLoadMore && !isDefined<string>(currentParams.from))
        ) {
            throw Error(errorMessages.invalidLoadmoreParameters);
        }

        this.pendingBs.next(true);
        this.isLoadMore = isLoadMore;
        this.queryParamsBs
            .pipe(
                distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
                map(previousParams => removeEmpty<Params>({ ...previousParams, ...currentParams }, true)),
                take(1)
            )
            .subscribe(queryparams => {
                // 'from' should only be a part of the request when using load more
                if (!isLoadMore) {
                    delete queryparams.from;
                }

                this.queryParamsBs.next(queryparams);

                // If we are in load more state we add the total amount of products
                // To the 'take' parameter in the url. Should the user refresh they will
                // Get the correct amount of products.
                // 'from' parameter should never be a part of the url so we always strip that
                const { from: fromParam, take: takeParam, ...rest } = queryparams;
                this.updateUrlState({
                    ...rest,
                    ...(this.isLoadMore ? { take: takeParam + fromParam } : {})
                });
            });
    }

    /**
     * Use this method to convert Params into filter params
     * @param params
     */
    public toFilterParams(params: Params | null): Params | null {
        return params && Object.keys(params).length ? { [this.filterParamKey]: JSON.stringify(params) } : null;
    }

    public reset(exceptions: Params = {}): void {
        this.pendingBs.next(true);
        this.isLoadMore = false;
        const resetObject = { take: this.defaultTakeAmount, ...exceptions };
        this.queryParamsBs.next(resetObject);
        this.updateUrlState(exceptions);
    }

    public destroy(): void {
        this.filterConfigurationId = null;
        this.queryParamsBs.next({});
        this.filterBs.next(this.intialState);
        this.filterListBs.next([]);
        this.errorRs.next(null);
        this.pendingBs.next(false);
    }

    public constructFilterUrl(params: Params | null): string {
        return this.router.serializeUrl(this.router.createUrlTree([], { queryParams: this.toFilterParams(params) }));
    }

    public trackingStockStoreEvents(postalCode?: string): void {
        if (postalCode) {
            this.gtmService.trackSeeStoreStockFilter(postalCode);
        }
    }

    private updateUrlState(params: Params | null) {
        this.location.replaceState(this.constructFilterUrl(params));
    }

    private get intialState(): Filter {
        return {
            facets: [],
            products: [],
            sortOptions: [],
            retailMediaProducts: {},
            total: 0,
            loadMore: false,
            initial: true
        };
    }
}
